/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useState } from 'react'

import { Alert, AssetDetailModal, SimpleTable, TableSkeleton } from '@/components/ui'
import { useApi, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin30Days } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import {
  ApiService,
  Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'
import { formatUserDate } from '@/utils/formatDate'

const DateOptions = [
  { label: 'Last Hour', value: 'lastHour' },
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'This Week', value: 'thisWeek' },
  { label: 'Last Week', value: 'lastWeek' },
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last 30 Days', value: 'previous1Month' },
  { label: 'Custom', value: 'custom' },
]

interface MonitoringPointHumidityParams extends InsightParametersType {
  companyId: number
  assetId: string
  speedType: string
  speedThreshold: number
  date?: string
  from?: string
  to?: string
}

interface MonitoringPointHumidityRecord {
  assetId: number
  assetName: string
  sampleTime: Date | string
  monitoringPointName: string
  value: number | null
}

const MonitoringPointHumidity: InsightDefinition<MonitoringPointHumidityParams, MonitoringPointHumidityRecord[]> = {
  active: true,
  slug: 'report/monitoring-point-humidity',
  category: 'report',
  name: 'InStore Monitoring Point Humidity',
  description: 'Report showing the humidity readings from monitoring points for selected assets.',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'facilityId',
        label: 'Facility',
        control: 'FacilityDropdownSingle',
      },
      {
        id: 'assetId',
        label: 'Location',
        control: 'AssetDropdownSingle',
        required: true,
        includeSelectAll: true,
        assetType: AssetTypeFilter.InStore,
      },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions,
      },
      {
        id: 'customDate',
        label: 'Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (configuration: MonitoringPointHumidityParams) => {
      if (configuration.date === 'custom' && !configuration.from && !configuration.to) {
        return 'Enter the custom date for Raised Time'
      }

      if (configuration.date === 'custom') {
        if (configuration.from && configuration.to) {
          const fromDate = new Date(configuration.from)
          const toDate = new Date(configuration.to)
          if (fromDate > toDate) {
            return 'Start date must be before end date'
          }
        }
        const dateValidation = validateDateRangeWithin30Days(configuration.from, configuration.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }

      if (configuration.companyId && configuration.assetId) {
        return true
      }

      return 'Company and Location are required.'
    },
  },

  load: async (api: ApiService, parameters: MonitoringPointHumidityParams) => {
    const path = `/insights/reports/monitoring-point-humidity?${buildQuery(parameters)}`
    const response = await api.get<MonitoringPointHumidityRecord[]>({ path, origin: 'MonitoringPointHumiditys' })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data?.length) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: MonitoringPointHumidityParams, title: string) => {
    const path = '/insights/reports/monitoring-point-humidity'
    await downloadCsv(api, path, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const parameters = configuration.parameters as MonitoringPointHumidityParams

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      // This could be handled by the framework by just returning the
      // same status. But we should make the skeleton match the final
      // table result if possible.
      return <TableSkeleton columns={3} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No history found".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }

    const monitoringPointNames = [...new Set(data.map((row) => row.monitoringPointName))]
    const groupedRows = new Map<
      string,
      {
        assetId: number
        assetName: string
        sampleTime: Date | string
        values: Map<string, number | null>
      }
    >()

    data.forEach((row) => {
      const sampleTime = new Date(row.sampleTime).toISOString()
      const key = `${row.assetId}:${sampleTime}`
      const grouped = groupedRows.get(key) ?? {
        assetId: row.assetId,
        assetName: row.assetName,
        sampleTime: row.sampleTime,
        values: new Map<string, number | null>(),
      }
      grouped.values.set(row.monitoringPointName, row.value)
      groupedRows.set(key, grouped)
    })

    const headers = ['Asset', 'Date', ...monitoringPointNames]
    const body = [...groupedRows.values()].map((row) => ({
      Asset: (
        <button onClick={() => setSelectedAsset(row.assetId)} className="text-left block text-blue-500 cursor-pointer">
          {row.assetName}
        </button>
      ),
      Date: formatUserDate(row.sampleTime),
      ...Object.fromEntries(monitoringPointNames.map((name) => [name, row.values.get(name) ?? ''])),
    }))

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} cellClassNames={{ Date: 'text-nowrap' }} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as MonitoringPointHumidityParams
    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/fresh/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false`,
            origin: 'BatteryVoltage#caption',
          })
          if (response.data?.length) {
            setAsset(response.data[0])
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default MonitoringPointHumidity
